import {
  BrandingAssetResponse,
  PublishedBrandingManifest,
  type BrandingVersionWaitResult,
  type PublishedBrandingManifest as PublishedBrandingManifestType,
} from "@ascleo/frontdesk-contracts/branding/public";
import type {
  BrandAssetHash,
  ResolvedBrandFont,
} from "@ascleo/frontdesk-contracts/branding/model";
import { Schema } from "effect";
import type { CSSProperties } from "react";

/**
 * Decoded immutable object stored by a shell branding cache.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { CachedBrandingAsset } from "@ascleo/frontdesk-shared/branding";
 *
 * function length(asset: CachedBrandingAsset) { return asset.metadata.byteLength; }
 * ```
 */
export const CachedBrandingAsset = Schema.Struct({
  metadata: BrandingAssetResponse,
  bytes: Schema.Uint8Array,
});

/**
 * Decoded cached immutable asset value.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { CachedBrandingAsset } from "@ascleo/frontdesk-shared/branding";
 *
 * function bytes(asset: CachedBrandingAsset) { return asset.bytes; }
 * ```
 */
export type CachedBrandingAsset = typeof CachedBrandingAsset.Type;

/**
 * Checked active-cache envelope committed through one atomic pointer transition.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { CachedBrandingRecord } from "@ascleo/frontdesk-shared/branding";
 *
 * function version(record: CachedBrandingRecord) { return record.manifest.publicationVersion; }
 * ```
 */
export const CachedBrandingRecord = Schema.Struct({
  cacheFormatVersion: Schema.Literal(1),
  contentFingerprint: Schema.String.pipe(Schema.check(Schema.isPattern(/^[0-9a-f]{64}$/))),
  referencedHashes: Schema.Array(
    Schema.String.pipe(Schema.check(Schema.isPattern(/^[0-9a-f]{64}$/))),
  ),
  manifest: PublishedBrandingManifest,
});

/**
 * Decoded active-cache envelope value.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { CachedBrandingRecord } from "@ascleo/frontdesk-shared/branding";
 *
 * function version(record: CachedBrandingRecord) { return record.manifest.publicationVersion; }
 * ```
 */
export type CachedBrandingRecord = typeof CachedBrandingRecord.Type;

/**
 * Shell request adapter for public SOL branding routes.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingTransport } from "@ascleo/frontdesk-shared/branding";
 *
 * function read(transport: BrandingTransport, signal: AbortSignal) { return transport.fetchManifest(signal); }
 * ```
 */
export interface BrandingTransport {
  readonly fetchManifest: (signal: AbortSignal) => Promise<unknown>;
  readonly fetchAsset: (
    hash: BrandAssetHash,
    signal: AbortSignal,
  ) => Promise<unknown>;
  readonly waitForChange: (
    afterVersion: number,
    signal: AbortSignal,
  ) => Promise<unknown>;
}

/**
 * Durable shell cache with an explicit stage-before-pointer-commit boundary.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingCache } from "@ascleo/frontdesk-shared/branding";
 *
 * function active(cache: BrandingCache) { return cache.readActive(); }
 * ```
 */
export interface BrandingCache {
  readonly readActive: () => Promise<unknown | undefined>;
  readonly readAsset: (hash: BrandAssetHash) => Promise<unknown | undefined>;
  readonly stageAsset: (asset: CachedBrandingAsset) => Promise<void>;
  readonly stageRecord: (record: CachedBrandingRecord) => Promise<void>;
  readonly commitActive: (publicationVersion: number) => Promise<void>;
  readonly abandon: (publicationVersion: number) => Promise<void>;
  readonly cleanup: (referencedHashes: ReadonlySet<string>) => Promise<void>;
}

/**
 * Opaque loaded font handle owned by a branding resource adapter.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { LoadedBrandFont } from "@ascleo/frontdesk-shared/branding";
 *
 * function family(font: LoadedBrandFont) { return font.family; }
 * ```
 */
export interface LoadedBrandFont {
  readonly family: string;
  readonly assetHash: BrandAssetHash;
}

/**
 * Browser resource staging boundary, replaceable with deterministic test doubles.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingResourceLoader } from "@ascleo/frontdesk-shared/branding";
 *
 * function revoke(loader: BrandingResourceLoader, url: string) { loader.revokeUrl(url); }
 * ```
 */
export interface BrandingResourceLoader {
  readonly createUrl: (asset: CachedBrandingAsset) => string;
  readonly loadImage: (url: string, signal: AbortSignal) => Promise<void>;
  readonly loadFont: (
    font: ResolvedBrandFont,
    assetHash: BrandAssetHash,
    url: string,
    signal: AbortSignal,
  ) => Promise<LoadedBrandFont>;
  readonly registerFont: (font: LoadedBrandFont) => void;
  readonly verifyFont: (font: LoadedBrandFont) => boolean;
  readonly removeFont: (font: LoadedBrandFont) => void;
  readonly revokeUrl: (url: string) => void;
}

/**
 * Safe, non-sensitive runtime lifecycle event.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingRuntimeEvent } from "@ascleo/frontdesk-shared/branding";
 *
 * const event: BrandingRuntimeEvent = { kind: "default-used" };
 * ```
 */
export type BrandingRuntimeEvent =
  | { readonly kind: "cache-used"; readonly version: number }
  | { readonly kind: "default-used" }
  | { readonly kind: "update-seen"; readonly version: number }
  | {
      readonly kind: "stage-failed";
      readonly category: "manifest" | "asset" | "image" | "font" | "cache";
    }
  | { readonly kind: "activated"; readonly version: number }
  | { readonly kind: "wait-unavailable" }
  | { readonly kind: "cleanup-failed" };

/**
 * Structured observer for safe branding lifecycle facts.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingRuntimeObserver } from "@ascleo/frontdesk-shared/branding";
 *
 * const observer: BrandingRuntimeObserver = { report: () => undefined };
 * ```
 */
export interface BrandingRuntimeObserver {
  readonly report: (event: BrandingRuntimeEvent) => void;
}

/**
 * Controls whether a branding runtime follows later publication changes after explicit staging.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const policy: BrandingSynchronizationPolicy = "bootstrap-only";
 * ```
 */
export type BrandingSynchronizationPolicy = "continuous" | "bootstrap-only";

/**
 * Complete dependency set for constructing a shared branding runtime.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingRuntimeDependencies } from "@ascleo/frontdesk-shared/branding";
 *
 * function transportOf(dependencies: BrandingRuntimeDependencies) { return dependencies.transport; }
 * ```
 */
export interface BrandingRuntimeDependencies {
  readonly transport: BrandingTransport;
  readonly cache: BrandingCache;
  readonly resources: BrandingResourceLoader;
  readonly synchronizationPolicy?: BrandingSynchronizationPolicy;
  readonly observer?: BrandingRuntimeObserver;
  readonly sleep?: (
    milliseconds: number,
    signal: AbortSignal,
  ) => Promise<void>;
  readonly random?: () => number;
  readonly deferRetirement?: (retire: () => void) => void;
}

/**
 * Fixed practice-scope CSS properties produced from decoded tokens only.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingPracticeStyle } from "@ascleo/frontdesk-shared/branding";
 *
 * const style: BrandingPracticeStyle = { "--primary": "#223381" };
 * ```
 */
export interface BrandingPracticeStyle extends CSSProperties {
  readonly "--primary"?: string;
  readonly "--primary-foreground"?: string;
  readonly "--primary-neon"?: string;
  readonly "--primary-soft"?: string;
  readonly "--primary-subtle"?: string;
  readonly "--primary-vivid"?: string;
  readonly "--primary-ink"?: string;
  readonly "--brand-accent"?: string;
  readonly "--brand-accent-foreground"?: string;
  readonly "--brand-accent-neon"?: string;
  readonly "--brand-accent-soft"?: string;
  readonly "--brand-accent-subtle"?: string;
  readonly "--brand-accent-vivid"?: string;
  readonly "--brand-accent-ink"?: string;
  readonly "--brand-supporting"?: string;
  readonly "--brand-supporting-foreground"?: string;
  readonly "--brand-supporting-neon"?: string;
  readonly "--brand-supporting-soft"?: string;
  readonly "--brand-supporting-subtle"?: string;
  readonly "--brand-supporting-vivid"?: string;
  readonly "--brand-supporting-ink"?: string;
  readonly "--accent"?: string;
  readonly "--accent-foreground"?: string;
  readonly "--font-sans"?: string;
  readonly "--font-heading"?: string;
}

/**
 * One fully staged and render-safe active practice identity.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { ActiveBrandingSnapshot } from "@ascleo/frontdesk-shared/branding";
 *
 * function source(snapshot: ActiveBrandingSnapshot) { return snapshot.source; }
 * ```
 */
export interface ActiveBrandingSnapshot {
  readonly source: "default" | "cache" | "remote";
  readonly manifest: PublishedBrandingManifestType;
  readonly style: BrandingPracticeStyle;
  readonly primaryLogoUrl?: string;
  readonly compactMarkUrl?: string;
  readonly visualDisplayName?: string;
  readonly headingFontFamily?: string;
  readonly bodyFontFamily?: string;
}

/**
 * Provider state separating active identity from non-fatal synchronization health.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingRuntimeSnapshot } from "@ascleo/frontdesk-shared/branding";
 *
 * function health(snapshot: BrandingRuntimeSnapshot) { return snapshot.syncHealth; }
 * ```
 */
export interface BrandingRuntimeSnapshot {
  readonly active: ActiveBrandingSnapshot;
  readonly syncHealth: "idle" | "synchronizing" | "healthy" | "degraded" | "stopped";
}

/**
 * Public shared runtime used unchanged by every FrontDesk+ shell.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingRuntime } from "@ascleo/frontdesk-shared/branding";
 *
 * function pause(runtime: BrandingRuntime) { runtime.pause(); }
 * ```
 */
export interface BrandingRuntime {
  readonly bootstrap: () => Promise<BrandingRuntimeSnapshot>;
  readonly synchronize: () => Promise<void>;
  readonly start: () => void;
  readonly pause: () => void;
  readonly resume: () => void;
  readonly stop: () => void;
  readonly getSnapshot: () => BrandingRuntimeSnapshot;
  readonly subscribe: (listener: () => void) => () => void;
}

/**
 * Narrow decoded wait result alias used by adapter implementations.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { DecodedBrandingWaitResult } from "@ascleo/frontdesk-shared/branding";
 *
 * const result: DecodedBrandingWaitResult = { outcome: "unchanged", publicationVersion: 0 };
 * ```
 */
export type DecodedBrandingWaitResult = BrandingVersionWaitResult;
